import React, { useState, useMemo, SetStateAction, Dispatch } from 'react';
import { finalize } from 'rxjs/operators';
import { throwError } from 'rxjs';
import CardBody from 'components/Card/CardBody';
import CustomPasswordInput from 'components/CustomInput/CustomPasswordInput';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { AuthorizationService } from 'services/authorization/authorization';
import { translate } from 'components/Translation';
import { makeStyles } from '@material-ui/core/styles';
import LockIcon from '@material-ui/icons/Lock';
import styles from 'assets/jss/material-dashboard-pro-react/views/changePasswordDialogStyle';
import { BodyM, HeadingMBold } from 'components/Typography/Typography';
import ErrorBox from 'components/ErrorBox/ErrorBox';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import CustomConfirmationDialog from 'components/CustomConfirmationDialog/CustomConfirmationDialog';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';
import { getChangePasswordLabels } from 'redux/modules/dynamicLabels/selectors';
import { getLoadingIndicator } from 'redux/modules/api/loadingIndicator/selector';
import Loader from 'components/Loader/Loader';
import { AuthConsumer } from 'components/Auth/authContext';
import ErrorStatusDialog from 'components/StatusDialog/ErrorStatusDialog';
import SuccessStatusDialog from 'components/StatusDialog/SuccessStatusDialog';
import ErrorMapper from 'utils/api/errors/errorMapper';
import { UsersService } from 'services/users/users';
import { OperationContextEnum } from 'utils/api/payloads/omantelApi';
import { setToast } from 'redux/modules/toast/actions';
import OTP from 'components/OTP/OTP';
import otpstyles from 'assets/jss/material-dashboard-pro-react/components/otpStyle';
import { getCommonLabels } from 'redux/modules/dynamicLabels/selectors';
import PasswordRules from './common/PasswordRules';
import OtpTab from 'views/Numbers/Details/Settings/subcomponents/ResetAdslPasswordDialog/subcomponents/OtpTab';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';
import { WRONG_OTP } from 'utils/const/otp';

// @ts-ignore
const useStyles = makeStyles(styles);

interface FormData {
	oldPassword: string;
	newPassword: string;
	newPassword2: string;
}

interface IFormProps {
	formData: FormData;
	onChange: (name: keyof FormData, value: string) => void;
	isValid: boolean;
	onSubmit: () => void;
	isResetSuccessful: boolean;
	isNewDashBoardEnabled?: boolean;
	setServerErrorMessage: Dispatch<SetStateAction<string>>;
	serverErrorMessage: string;
	setRulePassed: (pass: boolean) => void;
}

export const Form = (props: IFormProps) => {
	const {
		formData,
		onChange,
		onSubmit,
		isResetSuccessful,
		isNewDashBoardEnabled,
		serverErrorMessage,
		setServerErrorMessage,
	} = props;
	const classes = useStyles({ isNewDashBoardEnabled });
	const isLoaderVisible = useSelector(getLoadingIndicator);
	const changePasswordLabels = useSelector(getChangePasswordLabels);
	const commonLabels = useSelector(getCommonLabels);
	const errorMessage = useMemo(() => {
		let errorMessage = '';
		if (formData.oldPassword === formData.newPassword) {
			errorMessage = commonLabels.passwordCantBeSameAsOld;
		} else if (formData.newPassword !== formData.newPassword2) {
			errorMessage = commonLabels.passwordsNotMatched;
		}
		return errorMessage;
	}, [formData]);

	const headerText = (
		<React.Fragment>
			<BodyM color="primary-light" customClassName={classes.modalSubtitle}>
				{isNewDashBoardEnabled ? changePasswordLabels?.header : translate('change.password.form.body')}
			</BodyM>
		</React.Fragment>
	);
	const loginUnsuccessfulText = (
		<div className={classes.loginUnsuccessful}>{errorMessage && <ErrorBox text={errorMessage} />}</div>
	);

	const onStatusModalClosed = () => {
		setServerErrorMessage('');
	};
	const variant = isNewDashBoardEnabled ? 'outlined' : '';
	const icon = isNewDashBoardEnabled ? null : <LockIcon />;

	return (
		<>
			<form
				style={{ maxWidth: '300px' }}
				onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
					event.preventDefault();
					onSubmit();
				}}
			>
				{isResetSuccessful ? headerText : loginUnsuccessfulText}
				<CustomPasswordInput
					labelText={translate('number.settings.old.password')}
					id="oldPassword"
					inputProps={{
						required: true,
						value: formData.oldPassword,
						type: 'password',
						onChange: (event: React.FormEvent<HTMLInputElement>) =>
							onChange('oldPassword', (event.target as HTMLInputElement).value),
					}}
					formControlProps={{
						fullWidth: true,
					}}
					icon={<LockIcon />}
				/>
				<CustomPasswordInput
					labelText={translate('number.settings.new.password')}
					id="newPassword"
					inputProps={{
						required: true,
						value: formData.newPassword,
						type: 'password',
						onChange: (event: React.FormEvent<HTMLInputElement>) =>
							onChange('newPassword', (event.target as HTMLInputElement).value),
					}}
					formControlProps={{
						fullWidth: true,
					}}
					icon={<LockIcon />}
				/>

				<CustomPasswordInput
					labelText={translate('number.settings.confirm.new.password')}
					id="newPassword2"
					inputProps={{
						required: true,
						value: formData.newPassword2,
						type: 'password',
						onChange: (event: React.FormEvent<HTMLInputElement>) =>
							onChange('newPassword2', (event.target as HTMLInputElement).value),
					}}
					formControlProps={{
						fullWidth: true,
					}}
					icon={<LockIcon />}
				/>
			</form>
			{<PasswordRules password={formData.newPassword} setRulePassed={props.setRulePassed} />}
		</>
	);
};

const ConfirmationPage = () => {
	return <span>{translate('forgot.password.changed')}</span>;
};

enum STEPS {
	FORM = 1,
	CONFIRMATION,
}

interface IChangePasswordPage {
	hideChangePassword: () => void;
	msisdn: string;
}

const ChangePasswordDialog = ({ hideChangePassword, msisdn }: IChangePasswordPage) => {
	const [step, setStep] = useState<STEPS>(STEPS.FORM);
	const [isResetSuccessful, setIsResetSuccessful] = useState(true);
	const [serverErrorMessage, setServerErrorMessage] = useState(''); // new state just to show new error modal
	const [rulePassed, setRulePassed] = useState(false);
	const [formData, setFormData] = useState<FormData>({
		oldPassword: '',
		newPassword: '',
		newPassword2: '',
	});
	const isNewDashBoardEnabled = useNewTheme();
	const [isOTPStep, setOTPStep] = useState(false);
	const [otp, setOtp] = useState('');
	const [isServerError, setIsServerError] = useState('');
	const [isIncorrectOtp, setIsInCorrectOtp] = useState(false);
	const dispatch = useDispatch();

	const requestForOtp = () => {
		setOtp('');
		setIsInCorrectOtp(false);
		setIsServerError('');
		setServerErrorMessage('');
		if (checkFormValidity) {
			dispatch(showLoadingIndicator());
			setIsInCorrectOtp(false);
			UsersService.requestForOtp(OperationContextEnum.CORPCHANGEPASSWORD, msisdn)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(() => {
					setToast({
						error: false,
						label: translate('otp.token.sent'),
					});
					setIsResetSuccessful(true);
					setOTPStep(true);
				});
		} else {
			setIsResetSuccessful(false);
		}
	};

	const onSubmit = () => {
		if (checkFormValidity) {
			dispatch(showLoadingIndicator());
			setIsInCorrectOtp(false);
			AuthorizationService.changePassword({
				oldpassword: formData.oldPassword,
				password: formData.newPassword,
				password2: formData.newPassword2,
				otp,
			})
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(
					() => {
						setStep(2);

						// Log out the user without redirecting to the login screen
						// Pass `true` for a silent logout
						AuthorizationService.logOut(true).subscribe(
							() => {},
							() => {}
						);
					},
					(error) => {
						// in new dashboard it should come for all server errors
						if (isNewDashBoardEnabled) {
							if (error.response?.code === WRONG_OTP) {
								setIsInCorrectOtp(true);
							} else {
								setIsResetSuccessful(false);
								setOTPStep(false);
								setIsServerError(ErrorMapper.mapToMessage(error.response.code));
							}
						} else if (error.status === 400) {
							if (error.response.code === 'PASSWORD_DOES_NOT_MATCH') {
								setOTPStep(false);
								setIsResetSuccessful(false);
								setIsServerError(ErrorMapper.mapToMessage(error.response.code));
							} else {
								// Not valid credentials
								const wrongOtp = error.response.code === ERROR_VALIDATING_TOKEN;
								setIsServerError(wrongOtp ? '' : ErrorMapper.mapToMessage(error.response.code));
								if (wrongOtp) {
									setIsInCorrectOtp(true);
								}
								return;
							}
						} else {
							hideChangePassword();
							setIsServerError(ErrorMapper.mapToMessage(error.response.code));
						}
						return throwError(error);
					}
				);
		} else {
			setIsResetSuccessful(false);
		}
	};
	const onChange = (name: keyof FormData, value: string) =>
		setFormData((formDataState) => ({ ...formDataState, [name]: value }));

	const isFormValid = useMemo(() => Object.keys(formData).every((key) => !!formData[key]), [formData]);

	const checkFormValidity = useMemo(() => {
		return (
			formData.oldPassword !== formData.newPassword &&
			formData.newPassword === formData.newPassword2 &&
			rulePassed
		);
	}, [formData]);

	// @ts-ignore
	const classes = makeStyles(styles)({ isNewDashBoardEnabled });
	// @ts-ignore
	const otpwrapperclasses = makeStyles(otpstyles)();

	return (
		<>
			{step === STEPS.CONFIRMATION ? (
				isNewDashBoardEnabled ? (
					<AuthConsumer>
						{({ logout }) => <SuccessStatusDialog operationType="password" onClose={logout} />}
					</AuthConsumer>
				) : (
					<CustomConfirmationDialog
						isOpen
						header={<ConfirmationPage />}
						hideDialog={() => {
							hideChangePassword();
							AuthorizationService.logOut().subscribe(
								() => {},
								() => {}
							);
						}}
					/>
				)
			) : (
				<CustomDialog
					isOpen
					hideDialog={hideChangePassword}
					leftSideButtons={
						isNewDashBoardEnabled
							? [
									{
										text: translate('cancel'),
										color: 'tertiary-light-v2',
										onClick: hideChangePassword,
									},
							  ]
							: undefined
					}
					rightSideButtons={[
						{
							text: isOTPStep ? translate('navbar.change.password') : translate('next'),
							color: isNewDashBoardEnabled ? 'primary-light-v2' : 'primary-light',
							onClick: () => {
								if (!isOTPStep) {
									requestForOtp();
								} else {
									onSubmit();
								}
							},
							disabled: isOTPStep ? otp.length !== 5 : !isFormValid || !rulePassed,
						},
					]}
					dialogBody={
						<>
							{!isOTPStep ? (
								<CardBody className={classes.cardBody}>
									{isServerError ? (
										<div className={classes.loginUnsuccessful}>
											<ErrorBox text={isServerError} />
										</div>
									) : (
										<></>
									)}
									<Form
										formData={formData}
										onChange={onChange}
										isValid={isFormValid}
										onSubmit={requestForOtp}
										isResetSuccessful={isResetSuccessful}
										setServerErrorMessage={setServerErrorMessage}
										serverErrorMessage={serverErrorMessage}
										setRulePassed={setRulePassed}
									/>
								</CardBody>
							) : (
								<CardBody className={classes.cardBody}>
									{isServerError ? (
										<div className={classes.loginUnsuccessful}>
											<ErrorBox text={isServerError} />
										</div>
									) : (
										<></>
									)}
									<HeadingMBold>{translate('number.settings.password.sent')}</HeadingMBold>
									<div className={otpwrapperclasses.otpWrapper}>
										{isNewDashBoardEnabled ? (
											<OTP
												otpValue={otp}
												setOtpValue={setOtp}
												isNewDashBoardEnabled={isNewDashBoardEnabled}
												resendOtp={requestForOtp}
												otpIncorrect={isIncorrectOtp}
												submitOTP={onSubmit}
											/>
										) : (
											<OtpTab
												otp={otp}
												setOtp={setOtp}
												sendOtp={requestForOtp}
												otpIncorrect={isIncorrectOtp}
												inOneInput={false}
												submitOTP={onSubmit}
											/>
										)}
									</div>
								</CardBody>
							)}
						</>
					}
					header={translate('navbar.change.password')}
					width={isNewDashBoardEnabled ? 545 : undefined}
					isNewDashBoardEnabled={isNewDashBoardEnabled}
				/>
			)}
		</>
	);
};

export default ChangePasswordDialog;
